import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * cn() — class name merger used by all shadcn/ui components.
 *
 * Combines clsx (conditional class logic) with tailwind-merge (deduplication of
 * conflicting Tailwind utilities like "p-2 p-4" → "p-4"). All shadcn scaffold
 * files import this; bespoke components can migrate to it incrementally.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/* ─────────────────────────────────────────────────────────────────────────────
   motion/react timing constants — Slate & Field Green design system.

   All animations are data-meaningful:
   - SPRING_NAV   → snappy layout transitions (sidebar pill, tabs)
   - EASE_ENTER   → elements arriving on screen
   - EASE_EXIT    → elements leaving screen (faster = feels responsive)
   - EASE_DATA    → data-driven animations (bar widths, counters)
   - STAGGER_LIST → staggered card/list entrance

   motion/react automatically respects prefers-reduced-motion.
───────────────────────────────────────────────────────────────────────────── */

/** Springy nav/layout animations: sidebar active pill, tab indicator. */
export const SPRING_NAV = {
  type: "spring" as const,
  stiffness: 400,
  damping: 30,
} satisfies object;

/** Standard element entrance (fade + slight translate). */
export const EASE_ENTER = {
  duration: 0.22,
  ease: [0.25, 0.1, 0.25, 1.0] as [number, number, number, number],
};

/** Standard element exit — faster than entrance for responsiveness. */
export const EASE_EXIT = {
  duration: 0.15,
  ease: [0.4, 0.0, 1.0, 1.0] as [number, number, number, number],
};

/** Data-driven animations: bar widths, count-ups, progress. */
export const EASE_DATA = {
  duration: 0.5,
  ease: [0.4, 0.0, 0.2, 1.0] as [number, number, number, number],
};

/** Dialog/modal scale+fade. */
export const EASE_MODAL = {
  duration: 0.15,
  ease: [0.25, 0.1, 0.25, 1.0] as [number, number, number, number],
};

/** Stagger children variants for card grids and lists. */
export const STAGGER_LIST = {
  hidden:  { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.04 },
  },
};

/** Individual item variants used inside STAGGER_LIST parent. */
export const STAGGER_ITEM = {
  hidden:  { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: EASE_ENTER },
};
